import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * PriceList : famille "règles de prix conditionnelles".
 *
 * Construite sur le PATRON asset (modèle "famille + type" de la bible). Le socle
 * ne connaît PAS de tarif concret (grille happy hour, remise fidélité, code
 * promo saisonnier) : il connaît la famille générique `priceList`, et le
 * vocabulaire métier est porté par le champ `type` (chaîne libre déclarée par
 * l'app : "grille", "promo", "remise"...). Une priceList regroupe un ensemble de
 * règles de prix, remises et promotions déclenchées par des conditions (profil,
 * période, horaire, quantité, canal, code).
 *
 * Sens des références : ce sont les AUTRES familles qui pointent la priceList,
 * pas l'inverse. `product` et `order` portent déjà `priceListId` (le tarif
 * appliqué), et il en ira de même pour booking, subscription, policy. Une grille
 * s'applique à PLUSIEURS pièces : stocker ici un productId ou un orderId unique
 * serait la mauvaise cardinalité (même doctrine que order pour ses pièces aval).
 * On ne porte donc aucun de ces ids côté priceList. Le périmètre visé (segment,
 * période, canal) vit dans `appliesTo`, les conditions chiffrées dans `rules`.
 *
 * Périmètre v0 (org-wide, même doctrine qu'asset) : rien de sensible signalé
 * dans cette famille. Les liens éventuels restent de simples ids (aucun import
 * d'un autre schéma de famille).
 *
 * Convention de patron (cf. asset) :
 *   - `priceListCoreFields`        : champs MÉTIER seuls (sans les champs standard).
 *   - `PriceListSchema`            : famille complète = champs standard + métier.
 *   - `PriceListCreateInputSchema` : entrée fournie par l'appelant à la création
 *     (les champs standard sont remplis par le SDK). Valide AVANT écriture.
 */

/** Champs métier de la famille priceList (hors champs standard du socle). */
export const priceListCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "grille", "promo", "remise"). */
  type: z.string().min(1),
  /** Règles de prix / remise, liste structurée pilotée par l'app (condition -> effet), optionnel. */
  rules: z.array(z.record(z.string(), z.unknown())).optional(),
  /** Périmètre d'application (segment, période, canal), structure libre pilotée par l'app, optionnel. */
  appliesTo: z.record(z.string(), z.unknown()).optional(),
  /** Code promotionnel à saisir pour déclencher la grille, optionnel. */
  promoCode: z.string().min(1).optional(),
  /** Priorité de résolution entre grilles concurrentes (plus haut = prioritaire), optionnel. */
  priority: z.number().int().optional(),
  /** Début de validité de la grille, optionnel. */
  validFrom: z.date().optional(),
  /** Fin de validité de la grille, optionnel. */
  validTo: z.date().optional(),
  /** Devise des montants de la grille (code ISO, ex. "EUR"), optionnel. */
  currency: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille priceList (champs standard + champs métier). */
export const PriceListSchema = z.object({
  ...baseEntityFields,
  ...priceListCoreFields,
});

/** Type d'une priceList. */
export type PriceList = z.infer<typeof PriceListSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const PriceListCreateInputSchema = z.object({
  ...priceListCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'une priceList. */
export type PriceListCreateInput = z.infer<typeof PriceListCreateInputSchema>;

/**
 * Schéma du patch de mise à jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont gérés par le SDK : aucun des deux n'est accepté ici.
 */
export const PriceListUpdateInputSchema = z
  .object({ ...priceListCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise à jour d'une priceList. */
export type PriceListUpdateInput = z.infer<typeof PriceListUpdateInputSchema>;
